/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isObjectLike from 'lodash/isObjectLike';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import objectHash from 'object-hash';

import AssetDownloader from '../../asset/downloader/asset-downloader';
import CommunicatorAnalytics from '../../communicator/analytics/communicator-analytics';
import CommunicatorLayout from '../../communicator/layout/communicator-layout';
import CommunicatorOrientation from '../../communicator/orientation/communicator-orientation';
import CommunicatorSettings from '../../communicator/settings/communicator-settings';
import CommunicatorStretch from '../../communicator/stretch/communicator-stretch';
import CommunicatorUtility from '../../communicator/utility/communicator-utility';
import HolidayParser from '../../holiday/holiday-parser';
import MediumCommunicator from '../../medium/communicator/medium-communicator';
import { getUri, linkAsset } from '../../medium/medium';
import Numeric from '../../numeric/numeric';
import { flatten } from '../../object/object-util';
import PlaylistCommunicator from '../../playlist/playlist-communicator';
import * as PowerParser from '../../power/power-parser';
import PromiseUtil from '../../promise/promise-util';
import * as RebootParser from '../../reboot/reboot-parser';
import Store from '../../store/store';
import InfoVariablesNamespace from '../../variables/info-variables-namespace';
import UserVariablesNamespace from '../../variables/user-variables-namespace';
import ContentTaskCommunicator from '../task/communicator/content-task-communicator';
import { ContentInfo } from './content-info';

/**
 * @import { Condition, ScreenConditions, VisualController } from '.'
 * @import { ReadonlyIdMap } from '../../../util.type'
 * @import { Logger } from '../../logger/logger'
 * @import { Medium } from '../../medium/medium'
 * @import { IVariablesNamespaceRegistry, VariablesValue } from '../../variables'
 * @import { ViewerRegistry } from '../../viewer/registry/'
 */

/**
 * @typedef {Pick<ContentInfo, 'media'>} IMediaContentInfo
 */

const unexpectedTargetFormatError = Error('unexpected target format');
const unexpectedActionFormatError = Error('unexpected action format');
const unexpectedConditionFormatError = Error('unexpected condition format');

export default class ContentInfoParser {
  /**
   * Nalinkuje assety uložené v persistentStore k mediím v contentInfo.
   * Linkování se děje přímou modifikací property Medium.asset (což je
   * vlastně dirty modifikace z pohledu JSData, ale u transient resourců
   * to vlastně nevadí) pro všechny položky v ContentInfo.media.
   * @param {ViewerRegistry} viewerRegistry
   * @param {Store} persistentStore Perzistentní read-only datový sklad s assety.
   * @param {ReadonlyIdMap<Medium>} media Media k nimž mají být nalinkované assety.
   * @returns {Promise<void>}
   */
  static linkAssetsToMedia(viewerRegistry, persistentStore, media) {
    return new Promise((resolve) =>
      resolve(
        PromiseUtil.allSerial(
          [...media.values()]
            // Vyfiltrování je stažitelných médií.
            .filter((m) => {
              const viewerType = viewerRegistry.find(m.type);
              const offlineable =
                !isNil(viewerType) && viewerType.isOfflineable();
              if (!offlineable) {
                m.asset = undefined;
              }
              return offlineable;
            })
            .map(
              (medium) => () =>
                Promise.resolve()
                  .then(() =>
                    // Assety, kterým se liší checksum (typicky widgety), se zde nenamapují k mediím.
                    // To vynutí stažení novější verze.
                    AssetDownloader.findAsset(persistentStore, getUri(medium), {
                      checksum: medium.checksum
                    })
                  )
                  // TODO: zalogovat chybu
                  .catch(() => undefined)
                  .then((asset) => linkAsset(medium, asset))
            )
        ).then(noop)
      )
    );
  }

  // TODO: Seřadit parametry tak, aby první byly závislosti a až pak proměnné věci?
  /**
   * Naparsuje stažené content settings z Ki-Wi Serveru.
   * @param {Logger} logger Logger pro zaznamenávání chyb.
   * @param {ViewerRegistry} viewerRegistry Viewer registry pro filtrování stažitelných médií.
   * @param {string} rawJson JSON settings získané z Ki-Wi Serveru.
   * @param {Store} persistentStore Perzistentní read-only datový sklad s assety.
   * @param {IVariablesNamespaceRegistry} variablesNamespaceRegistry Registry proměnných pro URI template.
   * @param {Store} [contentStore] Transient store pro obsah (použití zejména v testech).
   * @returns {Promise<ContentInfo>} Settings transformované do objektu ContentInfo.
   */
  static async parse(
    logger,
    viewerRegistry,
    rawJson,
    persistentStore,
    variablesNamespaceRegistry,
    contentStore = new Store()
  ) {
    const contentInfo = this.parseContentInfo(
      logger,
      rawJson,
      variablesNamespaceRegistry,
      contentStore
    );
    await this.linkAssetsToMedia(
      viewerRegistry,
      persistentStore,
      contentInfo.media
    );
    return contentInfo;
  }

  /**
   * Naparsuje JSON string jako ContentInfo.
   *
   * Pozor na to, že je to synchronní metoda a nedělá linkování perzistentně
   * uložených assetů k médiím, jelikož nalezení assetů je asynchronní. Toto
   * linkování se děje volajícím, tj. ContentInfoParser.parse.
   * @private
   * @param {Logger} logger
   * @param {string} rawContentSettings Raw content settings JSON k parsování.
   * @param {IVariablesNamespaceRegistry} variablesNamespaceRegistry
   * @param {Store} [contentStore] Transient store pro obsah (použití zejména v testech).
   * @returns {ContentInfo} Převedené settings na ContentInfo objekt.
   * @throws {SyntaxError} Pokud není JSON validní.
   * @throws {TypeError} Pokud nějaká z očekávaných položek chybí nebo má nesprávný typ.
   */
  // eslint-disable-next-line max-statements
  static parseContentInfo(
    logger,
    rawContentSettings,
    variablesNamespaceRegistry,
    contentStore = new Store()
  ) {
    if (!isString(rawContentSettings)) {
      throw new TypeError('contentSettingsRawJson is not string');
    }
    const json = JSON.parse(rawContentSettings);

    // Načtení nastavení zařízení ve formě flat key/value záznamů.
    const communicatorSettings = new CommunicatorSettings(
      get(json, 'main.module.media[0].settings')
    );

    /** @type {any[]} */
    const contentTasksJson = get(json, 'main.commonContent.task') ?? [];
    const contentTasksAttrs =
      ContentTaskCommunicator.parseMany(contentTasksJson);
    const contentTasks = contentStore.injectContentTaskArray(contentTasksAttrs);

    // Pozor, obsahuje všechny typy tasků, nejen shutdown!
    /** @type {any[]} */
    const tasksJson = get(json, 'main.module.task') ?? [];
    const power = PowerParser.parse(tasksJson);
    const rebootTasks = RebootParser.parse(tasksJson);

    /** @type {any[]} */
    const holidaysJson = get(json, 'main.module.holiday') ?? [];
    const holidayIntervals = HolidayParser.parseMany(logger, holidaysJson);

    /** @type {any[]} */
    const mediaJson = get(json, 'main.commonContent.medium') ?? [];
    const mediaAttrs = MediumCommunicator.parseMany(mediaJson);
    const media = contentStore.injectMediumArray(mediaAttrs);

    /** @type {any[]} */
    const layoutArrayJson = get(json, 'main.commonContent.layout') ?? [];
    // CommunicatorLayout.parseMany řeší odfiltrování typově nekompatibilního
    // a nepotřebného mainLayout.
    const layouts = CommunicatorLayout.parseMany(layoutArrayJson);
    // TODO: Předělat layers ze samostatného resource na embedded collection
    // v layout - tím se zjednoduší zbytečné generování stabilního id,
    // vazby, atd.
    contentStore.injectLayoutArray(layouts);

    /** @type {any[]} */
    const playlistArrayJson = get(json, 'main.commonContent.playlist') ?? [];
    const playlists = PlaylistCommunicator.parseMany(playlistArrayJson);
    // TODO: Předělat tracks ze samostatného resource na embedded collection
    // v playlist - tím se zjednoduší zbytečné generování stabilního id,
    // vazby, atd.
    contentStore.injectPlaylistArray(playlists);

    const visualJson = get(json, 'main.visualController');

    // Nyní (po #389) se ignoruje délka nastavení analytické periody z Ki-Wi
    // Serveru a vždy je pevně 1h a analytika se zapíná tehdy, když přijde
    // libovolná definovaná hodnota (což v CommunicatorAnalytics.parse je
    // type === 'server' a frequency > 0).
    const analyticsEnabled =
      CommunicatorAnalytics.parse(get(json, 'main.module.media[0].log')) !==
      undefined;

    const backlight = CommunicatorUtility.parsePercentageOrUndefined(
      get(visualJson, 'screen[0].backlight')
    );
    const orientationEnum = CommunicatorOrientation.parse(
      get(visualJson, 'screen[0].orientation')
    );

    // TODO: Možná by to u povinných položek chtělo i logovat,
    // že hodnota z nějakého důvodu chyběla a jaká byla raw hodnota.
    const stretchEnum = CommunicatorStretch.parse(
      get(json, 'main.module.media[0].stretching')
    );
    if (isNil(stretchEnum)) {
      throw new TypeError('main.module.media[0].stretching');
    }

    const volume = CommunicatorUtility.parsePercentage(
      get(visualJson, 'volume')
    );

    const contentTimestamp = CommunicatorUtility.validateTimestampStringOrNull(
      get(json, 'main.info.updateTimestamp')
    );
    if (isNil(contentTimestamp)) {
      // TODO: Případně dodat hodnotu (výsledek get výše).
      throw new TypeError('main.info.updateTimestamp');
    }
    const info = parseInfo(get(json, 'main.info'));
    /** @type {string | undefined} */
    const serverUrl = get(json, 'main.info.serverUrl');
    /** @type {string | undefined} */
    const deviceName = get(json, 'main.info.name');

    variablesNamespaceRegistry.unregister('info');
    variablesNamespaceRegistry.unregister('user');
    variablesNamespaceRegistry.register(
      'info',
      new InfoVariablesNamespace(info)
    );
    variablesNamespaceRegistry.register(
      'user',
      new UserVariablesNamespace(communicatorSettings)
    );

    // Složení výsledného obsahu z jednotlivých částí.
    return new ContentInfo({
      analyticsEnabled,
      backlight,
      communicatorSettings,
      contentTasks,
      contentTimestamp,
      deviceName,
      holidayIntervals,
      info,
      layouts,
      media,
      orientationEnum,
      playlists,
      power,
      rebootTasks,
      serverUrl:
        serverUrl === undefined
          ? undefined
          : CommunicatorUtility.parseStringUrl(serverUrl),
      stretchEnum,
      visualController: visualControllerParser(logger, visualJson),
      volume
    });
  }

  static internal = {
    linkAssetsToMedia: this.linkAssetsToMedia
  };
}

/**
 * @param {any} jsonInfo
 * @returns {Record<string, VariablesValue>}
 */
function parseInfo(jsonInfo) {
  if (isNil(jsonInfo) || !isObjectLike(jsonInfo)) {
    return {};
  }
  const flat = flatten(jsonInfo);
  /** @type {Record<string, VariablesValue>} */
  const result = {};
  for (const [key, value] of Object.entries(flat)) {
    if (
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      (Array.isArray(value) && value.every((v) => typeof v === 'string'))
    ) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * @param {string} screenId
 * @param {string} target
 * @returns {{fromTarget: string | undefined, toTarget: string}}
 * @throws {Error}
 */
export function parseTargets(screenId, target) {
  const trimmedTarget = target.trim();
  if (screenId === '' || trimmedTarget === '') {
    throw unexpectedTargetFormatError;
  }
  let fromTarget;
  let toTarget = trimmedTarget;
  // Kontrola formátru hodnoty targetu.
  const arrowIdx = trimmedTarget.indexOf('=>');
  if (arrowIdx !== -1) {
    fromTarget = trimmedTarget.slice(0, arrowIdx);
    toTarget = trimmedTarget.slice(arrowIdx + 2);
    if (fromTarget === '' || toTarget === '') {
      throw unexpectedTargetFormatError;
    }
  }
  const lowerScreenId = screenId.toLocaleLowerCase();
  if (fromTarget !== undefined) {
    fromTarget = `${lowerScreenId}.${fromTarget}`;
  }
  return {
    fromTarget,
    toTarget: `${lowerScreenId}.${toTarget}`
  };
}

/**
 * @param {{ action: string, actionValue: string; condition: string; conditionValue: string; screen: string; }[]} conditions
 * @returns {ScreenConditions}
 */
export function screenConditionParser(conditions) {
  const deduplicatedConditions = new Set();
  // Deduplikace pravidel.
  return conditions
    .filter(({ screen, condition }) => {
      const key = objectHash({ screen, condition });
      if (deduplicatedConditions.has(key)) {
        return false;
      }
      deduplicatedConditions.add(key);
      return true;
    })
    .reduce((screenConditions, rawCondition) => {
      const {
        action: actionRaw,
        actionValue,
        condition: conditionRaw,
        conditionValue,
        screen: screenId
      } = rawCondition;
      try {
        const targets = parseTargets(screenId, actionValue);
        /** @type {import('.').Action | undefined} */
        let action;
        switch (actionRaw) {
          case 'action.contentChange':
            action = {
              type: 'action.contentChange',
              fromTarget: targets.fromTarget,
              toTarget: targets.toTarget
            };
            break;
          default:
            break;
        }

        if (action === undefined) {
          throw unexpectedActionFormatError;
        }

        /** @type {Condition | undefined} */
        let condition;
        switch (conditionRaw) {
          case 'condition.keyPress':
            condition = {
              type: 'condition.keyPress',
              action,
              screen: screenId
            };
            break;
          case 'condition.limitExpire':
            condition = {
              type: 'condition.limitExpire',
              action,
              limitInSec: Numeric.parseIndex(conditionValue),
              screen: screenId
            };
            break;
          case 'condition.startApplication':
            condition = {
              type: 'condition.startApplication',
              action,
              screen: screenId
            };
            break;
          default:
            break;
        }

        if (condition === undefined) {
          throw unexpectedConditionFormatError;
        }
        screenConditions[screenId] = screenConditions[screenId] ?? {};
        screenConditions[screenId][condition.type] =
          screenConditions[screenId][condition.type] ?? {};

        screenConditions[screenId][condition.type] = condition;
        return screenConditions;
      } catch {
        // Každý nevalidní formát targetu způsobí ignorování podmínky.
        return screenConditions;
      }
    }, {});
}

/**
 * @param {Logger} logger
 * @param {any} visualJson
 * @returns {VisualController}
 */
export function visualControllerParser(logger, visualJson) {
  switch (visualJson.type) {
    case 'ModernController':
      return {
        type: 'ModernController',
        screenConditions: screenConditionParser(visualJson.conditions),
        superengineJs: visualJson.controllerSettings
      };
    case 'UniversalController':
      return {
        type: 'UniversalController',
        screenConditions: screenConditionParser(visualJson.conditions)
      };
    case 'BasicController':
      break;
    default:
      logger.error(
        new Error(`${visualJson.type} is not supported controller.`)
      );
  }
  return {
    type: 'BasicController'
  };
}
